import {
  Alert,
  Button,
  InputNumber,
  message,
  Select,
  Skeleton,
  Spin,
  Typography,
} from 'antd'
import { useRegenerateRawData } from 'features/conduitApi'
import { useComputeLocalThinAreas } from 'features/fde/fitnessForServiceAPI'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import {
  buildLocalThinAreaIndicationSetEntry,
  computeBinSizeBreakdown,
  getAssetOuterDiameter,
  toMeasurement,
} from 'features/libra/stepComponents/ffs/binningCalculations'
import {
  buildDataFrameSettingsOptions,
  getCdsDataFrameSettings,
  getInspectionLengthUnit,
  USER_DEFINED_CSCAN_KEY,
} from 'features/libra/stepComponents/ffs/binningCdsHelpers'
import { FcaCalculatorModal } from 'features/libra/stepComponents/ffs/FcaCalculatorModal'
import { resolveFfsArtifactIds } from 'features/libra/stepComponents/ffs/ffsIds'
import { useFfsBinningValidation } from 'features/libra/stepComponents/ffs/useFfsBinningValidation'
import { useFfsComponentCdfs } from 'features/libra/stepComponents/ffs/useFfsComponentCdfs'
import { useFfsMarkupLayers } from 'features/libra/stepComponents/ffs/useFfsMarkupLayers'
import { useFfsTicketContext } from 'features/libra/stepComponents/ffs/useFfsTicketContext'
import { useFfsTriageData } from 'features/libra/stepComponents/ffs/useFfsTriageData'
import { useReportValidation } from 'features/libra/StepValidationContext'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useTicketWriter } from 'features/libra/ticket/TicketWriterContext'
import { LibraWorflowStepComponentProps } from 'features/libra/workflow/workflowStepsApi'
import { useComponentScope } from 'features/libra/workspace/ComponentScopeContext'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  GeckoMeasurement,
  GeckoMeasurementUnit,
  RolodexEntryPutJson,
  RolodexEntryType,
} from 'types'
import {
  buildFfsAnnex4bBinningConfigurationData,
  measurementFromConfigJson,
} from 'types/entries/geckoFfsAnnex4bBinningConfiguration'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
`

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const Label = styled.span`
  width: 13rem;
  font-weight: 500;
`

const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.forecast.border2 ?? '#444'};
  border-radius: 0.5rem;
`

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
`

const GENERATION_PHASE = {
  IDLE: 'idle',
  SAVING_CONFIG: 'saving-config',
  GENERATING_LTAS: 'generating-ltas',
  REFRESHING_VIZ: 'refreshing-viz',
} as const
type GenerationPhase = (typeof GENERATION_PHASE)[keyof typeof GENERATION_PHASE]

const GENERATION_PHASE_MESSAGE: Record<
  Exclude<GenerationPhase, typeof GENERATION_PHASE.IDLE>,
  string
> = {
  [GENERATION_PHASE.SAVING_CONFIG]: 'Saving FFS inputs...',
  [GENERATION_PHASE.GENERATING_LTAS]:
    'Generating Local Thin Areas and flaw paths...',
  [GENERATION_PHASE.REFRESHING_VIZ]: 'Refreshing visualization overlays...',
}

/**
 * Binning + LTA generation step.
 *
 * Mirrors the operational behavior of `FfsLtaGenerationSidebar.tsx` while
 * staying inside libra (no `features/->pages/` imports):
 *  - `D` derived (read-only) from the asset model via `getAssetOuterDiameter`
 *  - `t_rd` / `FCA` / `LTA threshold` pre-fill from the saved
 *    `ffs-configuration_{ticketId}_{componentId}` entry on mount (legacy
 *    `ffs-configuration_{componentId}` entries are read as a fallback when
 *    no `ComponentScopeContext` is mounted; see `ffsIds.ts`)
 *  - FCA-calculator modal (CR * time -> FCA)
 *  - **Save L_a Bin Sizes**: PUT ffs-config -> PATCH inspection bin sizes ->
 *    `useRegenerateRawData` (Conduit re-aggregation, async)
 *  - **Bin size dropdown**: populated from the CDS's `data_frame_settings`;
 *    SWR auto-revalidation picks up new options when Conduit finishes
 *  - **Generate Local Thin Areas**: re-persist config -> PUT LTA indication-set
 *    (with full detailCards) -> call `useComputeLocalThinAreas` against the
 *    selected `data_frame_settings` key.
 */
export const Component: FC<
  LibraWorflowStepComponentProps<Record<string, never>>
> = ({ readonly = false }) => {
  const readOnly = readonly
  const ticketAccess = useTicketAccess()
  // Asset (tabbed) tickets use ticket-scoped ids for FFS configuration and
  // LTA indication sets; legacy linear tickets without a
  // `ComponentScopeContext` keep the unscoped legacy ids so their writes
  // round-trip with the existing read paths in `useFfsBinningValidation`
  // and `useActiveLtaSet`.
  const scope = useComponentScope()
  const {
    componentId,
    component,
    inspection,
    inspectionId,
    unitId,
    isLoading: ticketLoading,
  } = useFfsTicketContext()

  const {
    validation,
    ffsConfig: savedFfsConfig,
    isLoading: binningLoading,
    forceRefresh: refreshBinningValidation,
  } = useFfsBinningValidation({
    component,
    componentId,
    inspectionId,
    unitId,
    inspection,
  })
  const isLoading = ticketLoading || binningLoading

  useReportValidation(validation)

  const {
    ltaSet,
    indications,
    forceRefresh: refreshTriageIndications,
  } = useFfsTriageData({ component, inspectionId })
  const { forceRefresh: refreshFfsMarkupLayers } = useFfsMarkupLayers({
    indications,
    ltaSetId: ltaSet?.id,
    component,
    inspectionId,
  })

  const [messageApi, messageContextHolder] = message.useMessage()
  const { rolodexPut, rolodexPatch } = useRolodexEdit()
  const { enqueue: enqueueTicketWrite } = useTicketWriter()
  const { regenerateRawData } = useRegenerateRawData()
  const {
    computeLocalThinAreas,
    isComputingLocalThinAreas,
    isConfigured: isFfsServiceConfigured,
  } = useComputeLocalThinAreas()

  const lengthUnit = GeckoMeasurementUnit.Millimeters
  const ffsUnit = GeckoMeasurementUnit.Millimeters

  // Shared CDF + dataset discovery -- same SWR cache key as FfsAssetVizPanel +
  // useObservedMinThickness, so cross-step navigation deduplicates the fetch.
  const {
    assetModel,
    componentDatasets,
    refresh: refreshComponentCdfs,
  } = useFfsComponentCdfs({ componentId, inspectionId })
  const selectedComponentModel = componentId
    ? assetModel?.data.components.get(componentId)
    : undefined
  const assetOuterDiameter = useMemo<GeckoMeasurement | null>(
    () => getAssetOuterDiameter(selectedComponentModel, lengthUnit),
    [lengthUnit, selectedComponentModel]
  )

  // Generate is gated on the CDS's user-defined data-frame settings being
  // present. When multiple datasets exist for the (component, inspection)
  // pair we sort by id for a deterministic pick AND surface a warning so
  // the engineer can review the selection.
  const sortedComponentDatasets = useMemo(
    () => [...componentDatasets].sort((a, b) => a.id.localeCompare(b.id)),
    [componentDatasets]
  )
  const localThinAreaCds = sortedComponentDatasets[0]
  const hasMultipleCdsCandidates = sortedComponentDatasets.length > 1
  const allDataFrameSettings = getCdsDataFrameSettings(localThinAreaCds)
  const dataFrameSettingsOptions = useMemo(
    () => buildDataFrameSettingsOptions(allDataFrameSettings),
    [allDataFrameSettings]
  )

  const [selectedDataFrameKey, setSelectedDataFrameKey] = useState<
    string | undefined
  >(undefined)

  // Default the selector to the user-defined key when it appears; otherwise
  // fall back to the first available option.
  useEffect(() => {
    if (dataFrameSettingsOptions.length === 0) {
      if (selectedDataFrameKey !== undefined) setSelectedDataFrameKey(undefined)
      return
    }
    if (
      selectedDataFrameKey &&
      dataFrameSettingsOptions.some((o) => o.value === selectedDataFrameKey)
    ) {
      return
    }
    const userDefined = dataFrameSettingsOptions.find(
      (o) => o.value === USER_DEFINED_CSCAN_KEY
    )
    setSelectedDataFrameKey(
      userDefined?.value ?? dataFrameSettingsOptions[0]?.value
    )
  }, [dataFrameSettingsOptions, selectedDataFrameKey])

  // `savedFfsConfig` comes from `useFfsBinningValidation` above -- the
  // validation hook already fetches the config entry, so the step reuses
  // it for form prefill instead of re-querying.

  const [uniformThickness, setUniformThickness] =
    useState<GeckoMeasurement | null>(null)
  const [futureCorrosionAllowance, setFutureCorrosionAllowance] =
    useState<GeckoMeasurement | null>(null)
  const [localThinAreaThreshold, setLocalThinAreaThreshold] =
    useState<GeckoMeasurement | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [fcaModalOpen, setFcaModalOpen] = useState(false)
  const [generationPhase, setGenerationPhase] = useState<GenerationPhase>(
    GENERATION_PHASE.IDLE
  )
  const isGeneratingLtas =
    generationPhase !== GENERATION_PHASE.IDLE || isComputingLocalThinAreas

  // Hydrate the form from the saved config when it loads. When no saved
  // config exists (e.g. component change in-place without remount), clear
  // any prior form state so the next ticket starts from a blank slate.
  useEffect(() => {
    if (!savedFfsConfig) {
      setUniformThickness(null)
      setFutureCorrosionAllowance(null)
      setLocalThinAreaThreshold(null)
      return
    }
    // Values are self-describing (`{ num, unit }`); `thicknessUnitId` upgrades
    // any legacy bare numbers written before the measurement migration.
    const fallbackUnitId = savedFfsConfig.thicknessUnitId
    setUniformThickness(
      measurementFromConfigJson(savedFfsConfig.uniformThickness, fallbackUnitId)
    )
    setFutureCorrosionAllowance(
      measurementFromConfigJson(
        savedFfsConfig.futureCorrosionAllowance,
        fallbackUnitId
      )
    )
    setLocalThinAreaThreshold(
      measurementFromConfigJson(
        savedFfsConfig.localThinAreaThreshold,
        fallbackUnitId
      )
    )
  }, [savedFfsConfig])

  const binSizeBreakdown = useMemo(
    () =>
      computeBinSizeBreakdown({
        assetOuterDiameter,
        uniformThickness,
        futureCorrosionAllowance,
        lengthUnit,
      }),
    [assetOuterDiameter, futureCorrosionAllowance, lengthUnit, uniformThickness]
  )
  const binSizeMm = binSizeBreakdown?.binSize ?? null

  const inspectionBinSizeUnit = getInspectionLengthUnit(
    inspection?.data.units as string | undefined
  )
  const canDeriveD = assetOuterDiameter !== null

  // `extraEntries` ride in the SAME `rolodexPut` as the config so callers
  // that persist related artifacts (the generate flow's LTA set) get one
  // atomic write instead of a config-updated/set-missing half-state.
  const persistFfsConfig = useCallback(
    async (extraEntries: RolodexEntryPutJson[] = []) => {
      if (readOnly) return false
      if (
        !component ||
        uniformThickness === null ||
        futureCorrosionAllowance === null
      ) {
        return false
      }
      // Record the interaction BEFORE the external Rolodex write. This is
      // deliberate: if the lifecycle transition itself fails, we abort the
      // chain before creating an orphan FFS config artifact. Accepted
      // trade-off: a SUCCESSFUL lifecycle update followed by a FAILED
      // `rolodexPut` leaves the ticket marked as touched (`ticketStatus`
      // advanced, `contributors` appended) even though the visible save
      // failed. Net call:
      //   (a) The user did interact and the audit trail should reflect
      //       the attempt.
      //   (b) Retrying the save is idempotent — no orphan artifact, no
      //       duplicate transition (FIRST_INTERACTION is one-shot per
      //       lifecycle stage).
      //   (c) Swapping the order (PUT first, interaction on success only)
      //       would silently drop the user's attempt from the audit trail
      //       on network errors.
      // The no-op patch (`return null`) tells the writer to skip emitting
      // a field write while still merging FIRST_INTERACTION + contributor
      // side effects.
      await enqueueTicketWrite(() => null, {
        applyInteractionSideEffects: true,
      })
      const { ffsConfigId } = resolveFfsArtifactIds({
        scoped: Boolean(scope),
        ticketId: ticketAccess.ticketId,
        componentId: component.id,
        unit: component.tags.unit,
        inspection: inspectionId ?? '',
      })
      await rolodexPut([
        {
          id: ffsConfigId,
          type: RolodexEntryType.FFS_CONFIGURATION,
          data: buildFfsAnnex4bBinningConfigurationData({
            thicknessUnitId: ffsUnit.id,
            uniformThickness: uniformThickness.convertTo(ffsUnit).toJSON(),
            futureCorrosionAllowance: futureCorrosionAllowance
              .convertTo(ffsUnit)
              .toJSON(),
            localThinAreaThreshold: localThinAreaThreshold
              ?.convertTo(ffsUnit)
              .toJSON(),
          }),
          tags: {
            [RolodexEntryType.ORGANIZATION]: component.tags.organization,
            [RolodexEntryType.SITE]: component.tags.site,
            [RolodexEntryType.UNIT]: component.tags.unit,
            [RolodexEntryType.COMPONENT]: component.id,
          },
        },
        ...extraEntries,
      ])
      return true
    },
    [
      component,
      ffsUnit,
      futureCorrosionAllowance,
      localThinAreaThreshold,
      readOnly,
      enqueueTicketWrite,
      inspectionId,
      rolodexPut,
      scope,
      ticketAccess.ticketId,
      uniformThickness,
    ]
  )

  const handleSaveBinSizes = async () => {
    if (readOnly) return
    if (!canDeriveD) {
      messageApi.error(
        'Add the component to the asset model in step 2 before saving bin sizes.'
      )
      return
    }
    if (!inspectionId || !inspectionBinSizeUnit) {
      messageApi.error('Inspection bin-size unit not available.')
      return
    }
    if (binSizeMm === null) {
      messageApi.error('Enter t_rd and FCA first.')
      return
    }
    setIsSaving(true)
    try {
      const ok = await persistFfsConfig()
      if (!ok) {
        messageApi.error('Enter t_rd and FCA before saving.')
        return
      }
      const inspectionBinSize = GeckoMeasurement.CreateFromValue(
        binSizeMm,
        lengthUnit
      ).convertTo(inspectionBinSizeUnit).num
      await rolodexPatch([
        {
          id: inspectionId,
          data: {
            x_bin_size: inspectionBinSize,
            y_bin_size: inspectionBinSize,
          },
        },
      ])
      try {
        await regenerateRawData(inspectionId)
      } catch {
        messageApi.warning(
          'Bin sizes saved, but Conduit re-aggregation could not be triggered.'
        )
      }
      messageApi.success('FFS configuration and bin sizes saved.')
      messageApi.info(
        'Conduit re-aggregation is running in the background -- bin-size options will update when it finishes.'
      )
      await Promise.all([refreshBinningValidation(), refreshComponentCdfs()])
    } catch {
      messageApi.error('Failed to save FFS configuration.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleGenerateLtas = async () => {
    if (readOnly) return
    if (!isFfsServiceConfigured) {
      messageApi.error('FFS service is not configured for this environment.')
      return
    }
    const thresholdMm =
      localThinAreaThreshold?.convertTo(lengthUnit).num ?? null
    if (
      !inspectionId ||
      !component ||
      !localThinAreaCds?.id ||
      !selectedDataFrameKey ||
      thresholdMm === null ||
      !Number.isFinite(thresholdMm) ||
      thresholdMm <= 0
    ) {
      messageApi.error(
        'CDS bin-size selection or a positive LTA threshold is required.'
      )
      return
    }
    setGenerationPhase(GENERATION_PHASE.SAVING_CONFIG)
    try {
      // Scoped-vs-legacy policy lives in `resolveFfsArtifactIds`; component
      // identity comes from `component.id` (the CDS is already filtered to
      // this component, so its tag is the same value -- one source, no
      // drift with `useActiveLtaSet`'s read side).
      const { ltaSetId } = resolveFfsArtifactIds({
        scoped: Boolean(scope),
        ticketId: ticketAccess.ticketId,
        componentId: component.id,
        unit: component.tags.unit,
        inspection: inspectionId,
      })
      const ltaSetEntry = buildLocalThinAreaIndicationSetEntry(
        localThinAreaCds,
        ltaSetId
      )
      // Config + LTA set land in ONE put -- no half-applied state where the
      // config updated but the set write failed.
      const ok = await persistFfsConfig([ltaSetEntry])
      if (!ok) {
        return
      }
      setGenerationPhase(GENERATION_PHASE.GENERATING_LTAS)
      const summary = await computeLocalThinAreas({
        cds_id: localThinAreaCds.id,
        data_frame_settings: selectedDataFrameKey,
        critical_threshold: thresholdMm,
        critical_threshold_unit: lengthUnit.id,
        indication_set: ltaSetEntry.id,
        create_flaw_boundaries: true,
        create_critical_thickness_profiles: true,
        create_rulers: true,
      })
      setGenerationPhase(GENERATION_PHASE.REFRESHING_VIZ)

      const refreshResults = await Promise.allSettled([
        refreshBinningValidation(),
        refreshTriageIndications(),
        refreshFfsMarkupLayers(),
      ])
      const refreshFailed = refreshResults.some((r) => r.status === 'rejected')

      if (refreshFailed) {
        messageApi.warning(
          'LTAs generated, but the viz pane could not refresh. Reload to see the latest overlays.'
        )
      } else {
        messageApi.success(
          `Local Thin Areas generated: ${summary.lta_count} LTAs and ${summary.entry_count} entries created.`
        )
      }
    } catch {
      messageApi.error('Failed to compute Local Thin Areas.')
    } finally {
      setGenerationPhase(GENERATION_PHASE.IDLE)
    }
  }

  if (isLoading) {
    return (
      <Container>
        <Skeleton active />
      </Container>
    )
  }

  return (
    <Container>
      {messageContextHolder}
      <Typography.Title level={4}>Binning & LTA Generation</Typography.Title>
      <Typography.Paragraph type='secondary'>
        Configure API 579 Annex 4B binning parameters and generate Local Thin
        Areas. All values in millimetres.
      </Typography.Paragraph>

      {!canDeriveD ? (
        <Alert
          type='error'
          showIcon
          message='Add the component to the asset model first.'
          description='`D` is derived from the asset model. Go back to step 2 and add the component, then return.'
        />
      ) : null}

      <SectionBlock>
        <FieldRow>
          <Label>D (outer diameter)</Label>
          <Typography.Text strong>
            {assetOuterDiameter
              ? `${assetOuterDiameter.convertTo(lengthUnit).num.toFixed(2)} mm`
              : '--'}
          </Typography.Text>
        </FieldRow>
        <FieldRow>
          <Label>t_rd (uniform thickness)</Label>
          <InputNumber
            value={uniformThickness?.convertTo(lengthUnit).num ?? null}
            onChange={(v) =>
              setUniformThickness(
                typeof v === 'number' ? toMeasurement(v, lengthUnit) : null
              )
            }
            step={0.1}
            min={0}
            addonAfter='mm'
            disabled={readOnly}
          />
        </FieldRow>
        <FieldRow>
          <Label>FCA</Label>
          <InputNumber
            value={futureCorrosionAllowance?.convertTo(lengthUnit).num ?? null}
            onChange={(v) =>
              setFutureCorrosionAllowance(
                typeof v === 'number' ? toMeasurement(v, lengthUnit) : null
              )
            }
            step={0.1}
            min={0}
            addonAfter='mm'
            disabled={readOnly}
          />
          <Button
            size='small'
            onClick={() => setFcaModalOpen(true)}
            disabled={readOnly}>
            Calculate
          </Button>
        </FieldRow>
        <FieldRow>
          <Label>t_c (corrected thickness)</Label>
          <Typography.Text strong>
            {binSizeBreakdown
              ? `${binSizeBreakdown.correctedThickness.toFixed(3)} mm`
              : '--'}
          </Typography.Text>
        </FieldRow>
        <FieldRow>
          <Label>L_s (screening length cap)</Label>
          <Typography.Text strong>
            {binSizeBreakdown
              ? `${binSizeBreakdown.readingSpacingUpperBound.toFixed(3)} mm`
              : '--'}
          </Typography.Text>
        </FieldRow>
        <FieldRow>
          <Label>L_a (bin size)</Label>
          <Typography.Text strong>
            {binSizeMm !== null
              ? `${binSizeMm.toFixed(3)} ${lengthUnit.id}${inspectionBinSizeUnit && inspectionBinSizeUnit.id !== lengthUnit.id ? ` (saved as ${inspectionBinSizeUnit.id})` : ''}`
              : '--'}
          </Typography.Text>
        </FieldRow>
      </SectionBlock>

      <ActionRow>
        <Button
          type='primary'
          loading={isSaving}
          onClick={handleSaveBinSizes}
          disabled={
            readOnly ||
            isGeneratingLtas ||
            !canDeriveD ||
            binSizeMm === null ||
            uniformThickness === null ||
            futureCorrosionAllowance === null
          }>
          Save L_a Bin Sizes
        </Button>
      </ActionRow>

      <SectionBlock>
        <FieldRow>
          <Label>LTA threshold</Label>
          <InputNumber
            value={localThinAreaThreshold?.convertTo(lengthUnit).num ?? null}
            onChange={(v) =>
              setLocalThinAreaThreshold(
                typeof v === 'number' ? toMeasurement(v, lengthUnit) : null
              )
            }
            step={0.1}
            min={0}
            addonAfter='mm'
            disabled={readOnly}
          />
        </FieldRow>
        <FieldRow>
          <Label>Bin size</Label>
          <Select
            value={selectedDataFrameKey}
            onChange={setSelectedDataFrameKey}
            options={dataFrameSettingsOptions}
            disabled={readOnly || dataFrameSettingsOptions.length === 0}
            placeholder={
              dataFrameSettingsOptions.length === 0
                ? 'No bin-size options available'
                : 'Select bin size'
            }
            style={{ flex: 1, minWidth: '14rem' }}
          />
        </FieldRow>
      </SectionBlock>

      {!isFfsServiceConfigured ? (
        <Alert
          type='info'
          showIcon
          message='FFS service not configured.'
          description='Set FFS_SERVICE_URL in the deployment to enable LTA generation.'
        />
      ) : null}

      {!localThinAreaCds ? (
        <Alert
          type='warning'
          showIcon
          message='No CDS found for this inspection.'
          description='LTA generation requires a component dataset for the (component, inspection) pair.'
        />
      ) : hasMultipleCdsCandidates ? (
        <Alert
          type='warning'
          showIcon
          message={`Multiple component datasets found (${sortedComponentDatasets.length}). Using "${localThinAreaCds.id}" (lowest id).`}
          description='Verify the selected CDS is the intended one before generating LTAs.'
        />
      ) : dataFrameSettingsOptions.length === 0 ? (
        <Alert
          type='info'
          showIcon
          message='No bin-size options yet.'
          description='After saving L_a bin sizes, Conduit re-aggregation runs in the background (typically minutes). The bin-size dropdown will populate when it finishes.'
        />
      ) : null}

      {generationPhase !== GENERATION_PHASE.IDLE ? (
        <Alert
          type='info'
          showIcon
          icon={<Spin size='small' />}
          message={GENERATION_PHASE_MESSAGE[generationPhase]}
        />
      ) : null}

      <ActionRow>
        <Button
          type='primary'
          loading={isGeneratingLtas}
          onClick={handleGenerateLtas}
          disabled={
            readOnly ||
            isGeneratingLtas ||
            !isFfsServiceConfigured ||
            !localThinAreaCds ||
            !selectedDataFrameKey ||
            localThinAreaThreshold === null ||
            !Number.isFinite(
              localThinAreaThreshold?.convertTo(lengthUnit).num
            ) ||
            (localThinAreaThreshold?.convertTo(lengthUnit).num ?? 0) <= 0
          }>
          Generate Local Thin Areas
        </Button>
      </ActionRow>

      <FcaCalculatorModal
        open={fcaModalOpen}
        lengthUnit={lengthUnit}
        onClose={() => setFcaModalOpen(false)}
        onApply={(value) => {
          setFutureCorrosionAllowance(value)
          setFcaModalOpen(false)
        }}
      />
    </Container>
  )
}
